import React, { useState } from 'react';
import { UI, FONT} from "../../constants/tokens";
import Icon from '../Icon';
import { openPrivacy } from '../../lib/privacyRoute';
import logo256 from '../../assets/suricata-logo-256.png';

const ERR_MAP = {
  'Invalid login credentials':                       'אימייל או סיסמה שגויים',
  'Email not confirmed':                             'האימייל לא אומת · בדוק את תיבת הדואר',
  'User already registered':                         'האימייל כבר רשום במערכת',
  'Password should be at least 6 characters':        'הסיסמה חייבת להכיל לפחות 6 תווים',
  'Unable to validate email address: invalid format':'כתובת האימייל אינה תקינה',
  'Signup requires a valid password':                'נדרשת סיסמה תקינה',
  'Email rate limit exceeded':                       'יותר מדי ניסיונות · המתן מספר דקות ונסה שנית',
  'For security purposes, you can only request this': 'בקשה נשלחה זה עתה · המתן מספר שניות ונסה שנית',
  'New password should be different':                'הסיסמה החדשה חייבת להיות שונה מהנוכחית',
  // Raised only when the Supabase password policy asks for more than length —
  // the forms check length locally, so these two arrive from the server alone.
  'Password should contain at least one character':  'הסיסמה אינה עומדת בדרישות · נדרש שילוב של אותיות, ספרות וסימנים',
  'known to be weak':                                'הסיסמה הזו מופיעה ברשימות סיסמאות שדלפו · בחר סיסמה אחרת',
  'Auth session missing':                            'הקשר פג או כבר נוצל · בקש קישור חדש',
  'Invalid Refresh Token':                           'הקשר פג או כבר נוצל · בקש קישור חדש',
  'Token has expired or is invalid':                 'הקשר פג או כבר נוצל · בקש קישור חדש',
  // A failed fetch reaches us as the browser's own wording, which differs per
  // engine: Chrome says 'Failed to fetch', Firefox 'NetworkError…', Safari
  // 'Load failed'. Without all three a dropped connection reads as an
  // unexplained error, and the user retries the one thing that cannot work.
  'Failed to fetch':                                 'אין חיבור לשרת · בדוק את החיבור לרשת ונסה שנית',
  'NetworkError':                                    'אין חיבור לשרת · בדוק את החיבור לרשת ונסה שנית',
  'Load failed':                                     'אין חיבור לשרת · בדוק את החיבור לרשת ונסה שנית',
  'Service temporarily unavailable':                 'השירות אינו זמין כרגע · נסה שנית בעוד מספר דקות',
  'service-unavailable':                             'השירות אינו זמין · פנה לתמיכה: nagardavid88@gmail.com',
};

// Exported so the reset screen speaks the same Hebrew for the same failures —
// two error dictionaries would drift the moment one of them gains an entry.
export function toHebrew(msg) {
  if (!msg) return 'אירעה שגיאה, נסה שנית';
  for (const [en, he] of Object.entries(ERR_MAP)) {
    if (msg.includes(en)) return he;
  }
  return 'אירעה שגיאה, נסה שנית';
}

export const INPUT = {
  width: '100%',
  background: UI.surface.raised,
  border: `1px solid ${UI.line.hairline}`,
  borderRadius: 6,
  padding: '11px 14px',
  fontSize: 16,
  color: UI.text.primary,
  outline: 'none',
  fontFamily: FONT.body,
  boxSizing: 'border-box',
  direction: 'ltr',
};

// A password box with its own reveal toggle. Exported because the reset screen
// needs the same control on two more fields, and a second copy is the kind that
// drifts — one side gains a fix and the other keeps the bug.
//
// `visible` lives inside the instance deliberately. The reset screen shows a new
// password and its confirmation side by side, and the whole point of the second
// field is to catch a typo in the first; a flag held by the parent would reveal
// both at once and hand the user a comparison that proves nothing.
export function PasswordField({ value, onChange, placeholder, autoComplete, required = true }) {
  const [visible, setVisible] = useState(false);
  const [lit, setLit] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        // Vertical padding is untouched, so the row keeps the exact height of
        // every other input on the card. Only the near edge grows, and only by
        // enough to clear the button.
        style={{ ...INPUT, paddingLeft: 42 }}
      />
      {/* Pinned to the left edge: the card is RTL, so that is where a Hebrew
          reader's eye leaves the field rather than where the cursor starts.
          Stretched top-to-bottom instead of centred by transform, so the hit
          area covers the full row and the icon needs no offset arithmetic. */}
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        onMouseEnter={() => setLit(true)}
        onMouseLeave={() => setLit(false)}
        onFocus={() => setLit(true)}
        onBlur={() => setLit(false)}
        aria-label={visible ? 'הסתר סיסמה' : 'הצג סיסמה'}
        style={{
          position: 'absolute', left: 4, top: 0, bottom: 0, width: 34,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, background: 'none', border: 'none', cursor: 'pointer',
          color: lit ? UI.text.primary : UI.text.secondary,
        }}
      >
        <Icon name={visible ? 'eyeOff' : 'eye'} size={18} />
      </button>
    </div>
  );
}

export default function AuthPage({ onSignIn, onSignUp, onGuest, onForgotPassword, degraded = false }) {
  // 'login' | 'signup' | 'forgot' — 'forgot' is reachable only from 'login' and
  // is not a tab, because it is a detour out of signing in rather than a third
  // thing the visitor came to do.
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState(null);
  const [info, setInfo]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);

  const switchMode = (m) => { setMode(m); setError(null); setInfo(null); };

  const handleGuest = async () => {
    if (!onGuest) return;
    setError(null);
    setInfo(null);
    setGuestBusy(true);
    const err = await onGuest();
    if (err) {
      setError(toHebrew(err.message));
      setGuestBusy(false);
    }
    // On success the auth state changes and Root swaps to <App/>, so no reset.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);

    if (mode === 'forgot') {
      const err = await onForgotPassword(email);
      if (err) {
        setError(toHebrew(err.message));
      } else {
        // Supabase answers identically for a registered and an unregistered
        // address, so this cannot say "a mail was sent to you" — that would be
        // a claim the app has no way to verify.
        setInfo('אם קיים חשבון עם הכתובת הזו · נשלח אליו קישור לאיפוס סיסמה. בדוק גם בתיקיית הספאם');
        setEmail('');
      }
    } else if (mode === 'login') {
      const err = await onSignIn(email, password);
      if (err) setError(toHebrew(err.message));
    } else {
      const err = await onSignUp(email, password);
      if (err) {
        setError(toHebrew(err.message));
      } else {
        setInfo('נשלח קישור אימות לאימייל שלך · בדוק את תיבת הדואר ואשר את החשבון');
        setEmail('');
        setPassword('');
      }
    }
    setBusy(false);
  };

  if (degraded) return (
    <div dir="rtl" style={{
      minHeight: '100dvh', background: UI.surface.page,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: FONT.body, padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <img src={logo256} alt="Suricata" style={{
          width: 52, height: 52, borderRadius: '22%', background: UI.logoPlate,
          objectFit: 'cover', marginBottom: 16,
        }}/>
        <div dir="ltr" style={{ fontWeight: 700, fontSize: 20, color: UI.text.primary, marginBottom: 8, fontFamily: FONT.latin, unicodeBidi: 'isolate' }}>Suricata</div>
        <div style={{
          background: UI.cautionSoft, border: `1px solid ${UI.cautionLine}`,
          borderRadius: 6, padding: '16px 20px', marginTop: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: UI.caution, marginBottom: 6 }}>השירות אינו זמין כרגע</div>
          <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.7 }}>
            תצורת השרת חסרה · פנה לתמיכה:<br/>
            {/* Has to be found, not just read — a categorical hue, not gold. */}
            <span style={{ color: UI.categorical[0], fontWeight: 700 }}>nagardavid88@gmail.com</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div dir="rtl" style={{
      minHeight: '100dvh',
      background: UI.surface.page,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT.body,
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={logo256} alt="Suricata" style={{
            width: 52, height: 52, borderRadius: '22%',
            background: UI.logoPlate, objectFit: 'cover',
            marginBottom: 12,
          }}/>
          <div dir="ltr" style={{ fontWeight: 700, fontSize: 22, color: UI.text.primary, letterSpacing: '-0.5px', fontFamily: FONT.latin, unicodeBidi: 'isolate' }}>
            Suricata
          </div>
          <div style={{ fontSize: 14, color: UI.text.secondary, marginTop: 4, fontWeight: 600 }}>
            ניהול תיק חכם
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: UI.surface.raised,
          border: `1px solid ${UI.line.hairline}`,
          borderRadius: 6,
          padding: '28px 24px',
        }}>

          {/* Mode Toggle — replaced by a titled header while recovering, where
              the two tabs would offer a choice the visitor is not making. */}
          {mode === 'forgot' ? (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: UI.text.primary, marginBottom: 6 }}>
                איפוס סיסמה
              </div>
              <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
                הזן את כתובת האימייל של החשבון · יישלח אליה קישור לבחירת סיסמה חדשה
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', background: UI.surface.page, borderRadius: 6, padding: 3, marginBottom: 24 }}>
              {[['login', 'כניסה'], ['signup', 'הרשמה']].map(([m, label]) => (
                <button key={m} onClick={() => switchMode(m)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 6, cursor: 'pointer',
                  border: mode === m ? `1px solid ${UI.line.strong}` : '1px solid transparent',
                  background: mode === m ? UI.fill.subtle : 'transparent',
                  color: mode === m ? UI.text.primary : UI.text.secondary,
                  fontWeight: mode === m ? 700 : 500, fontSize: 16,
                  fontFamily: FONT.body,
                }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: UI.text.secondary, fontWeight: 600, marginBottom: 6 }}>
                כתובת אימייל
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                style={INPUT}
              />
            </div>

            {mode !== 'forgot' && (
              <div>
                <label style={{ display: 'block', fontSize: 13, color: UI.text.secondary, fontWeight: 600, marginBottom: 6 }}>
                  סיסמה
                </label>
                <PasswordField
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'לפחות 6 תווים' : '••••••••'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                {mode === 'login' && onForgotPassword && (
                  <button type="button" onClick={() => switchMode('forgot')} style={{
                    background: 'none', border: 'none', padding: '8px 0 0', cursor: 'pointer',
                    fontSize: 13, color: UI.text.secondary, fontWeight: 600,
                    fontFamily: FONT.body, textDecoration: 'underline',
                  }}>
                    שכחתי סיסמה
                  </button>
                )}
              </div>
            )}

            {error && (
              <div style={{
                background: UI.cautionSoft,
                border: `1px solid ${UI.cautionLine}`,
                borderRadius: 6, padding: '9px 12px',
                fontSize: 14, color: UI.caution, fontWeight: 600,
              }}>
                {error}
              </div>
            )}

            {info && (
              <div style={{
                background: UI.fill.subtle,
                border: `1px solid ${UI.line.strong}`,
                borderRadius: 6, padding: '9px 12px',
                fontSize: 14, color: UI.text.primary, fontWeight: 600,
              }}>
                {info}
              </div>
            )}

            <button type="submit" disabled={busy} style={{
              width: '100%', padding: '12px 0',
              background: busy ? UI.fill.subtle : UI.accent.base,
              border: 'none', borderRadius: 6,
              cursor: busy ? 'default' : 'pointer',
              color: busy ? UI.text.secondary : UI.surface.page,
              fontWeight: 800, fontSize: 16,
              fontFamily: FONT.body,
              marginTop: 4,
            }}>
              {busy ? '...' : mode === 'login' ? 'כניסה' : mode === 'forgot' ? 'שלח קישור לאיפוס' : 'יצירת חשבון'}
            </button>

            {mode === 'forgot' && (
              <button type="button" onClick={() => switchMode('login')} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontSize: 13, color: UI.text.secondary, fontWeight: 600,
                fontFamily: FONT.body, textDecoration: 'underline',
              }}>
                חזרה לכניסה
              </button>
            )}
          </form>

          {/* Guest access — Supabase anonymous sign-in. Below the regular login,
              visually secondary. Seeds a private demo portfolio; data is not saved. */}
          {onGuest && mode !== 'forgot' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 16px' }}>
                <div style={{ flex: 1, height: 1, background: UI.fill.subtle }}/>
                <span style={{ fontSize: 12, color: UI.text.secondary, fontWeight: 600 }}>או</span>
                <div style={{ flex: 1, height: 1, background: UI.fill.subtle }}/>
              </div>
              <button type="button" onClick={handleGuest} disabled={guestBusy || busy} style={{
                width: '100%', padding: '12px 0',
                background: 'transparent',
                border: `1px solid ${UI.line.hairline}`, borderRadius: 6,
                cursor: guestBusy ? 'default' : 'pointer',
                color: guestBusy ? UI.text.secondary : UI.text.primary,
                fontWeight: 700, fontSize: 15,
                fontFamily: FONT.body,
              }}>
                {guestBusy ? '...' : 'כניסה כאורח · ללא הרשמה'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: UI.text.secondary, lineHeight: 1.6 }}>
                תיק לדוגמה · נתונים אמיתיים · אינו נשמר · מידע בלבד
              </div>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
          <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>Suricata · {new Date().getFullYear()}</span>
        </div>

        {/* Reachable before signing up on purpose: what gets collected is
            something to read before handing over an email, not after. */}
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button
            type="button"
            onClick={openPrivacy}
            style={{
              background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer',
              color: UI.text.secondary, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              textDecoration: 'underline', textUnderlineOffset: 3,
            }}>
            מדיניות פרטיות
          </button>
        </div>
      </div>
    </div>
  );
}
